import {DataObject} from "./DataObject";
import {copyBooleanProperties} from "./supporting/BooleanProperties";

/**
 * Stored representation of a credit card held by our merchant gateway.
 *
 * Important: the actual card number, CVV, and full expiration are NEVER stored
 * client-side. The merchant gateway returns a token (held in `value` and on the
 * server) that we use for subsequent charges. Only safe-to-display metadata
 * (brand, last4, exp month/year, cardholder name) is round-tripped to the UI.
 */
export class ChargeProfile implements DataObject {
  /**
   * Properties DECLARED `boolean` on this class. The generic loop in
   * `update()` keeps `false`, but it leaves a MySQL `TINYINT(1)` as the
   * number `0` / `1` sitting in a boolean-typed field.
   * See `supporting/BooleanProperties.ts`.
   */
  private static readonly BOOLEAN_PROPERTIES: string[] = [
    'default'
  ];

  ID: number;
  org_ID: number;
  /** User-facing label for this profile (e.g., "Company Visa"). */
  name: string;
  default: boolean;
  lastUpdate: Date;

  // ── Safe-to-display card metadata returned by the gateway ───────────────
  /** 'VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', etc. */
  cardBrand: string;
  /** Last four digits of the card number, safe to show. */
  last4: string;
  /** Expiration month 1-12. */
  expMonth: number;
  /** Four-digit expiration year. */
  expYear: number;
  /** Name on the card, as captured at tokenization time. */
  cardholderName: string;
  /** Billing postal code captured for AVS at tokenization time. */
  billingPostalCode: string;
  /**
   * Identifier for the merchant gateway that tokenized this card and now holds
   * the reusable token. Concrete live processor today is `'CARDPOINTE'` (see
   * `docs/contracts/inbox/_processed/2026-07-08-cardpointe-payment-processor-live.md`);
   * the server also accepts the aliases `'CARDCONNECT'`,
   * `'CARDCONNECT_GATEWAY'`, and `'CARD_POINTE'` and normalizes them to
   * `'CARDPOINTE'`. `'AUTHORIZENET'` and `'STRIPE'` remain scaffolded /
   * unimplemented in live mode. See `docs/contracts/shared/ChargeProfile.md`.
   */
  gateway: string;

  constructor (obj: any) {
    this.ID = 0;
    this.org_ID = 0;
    this.name = '';
    this.default = false;
    this.lastUpdate = new Date();
    this.cardBrand = '';
    this.last4 = '';
    this.expMonth = 0;
    this.expYear = 0;
    this.cardholderName = '';
    this.billingPostalCode = '';
    this.gateway = '';

    if (obj && obj['ID']) {
      this.ID = obj['ID'];
    }
    this.update(obj);
  }

  getId() {
    return this.ID;
  }

  getOrderValue() {
    return this.ID;
  }

  update (obj: any) {
    if (obj) {
      for (const prop of Object.keys(obj)) {
        if (obj[prop] !== null && (prop !== 'ID') && ( typeof obj[prop] === 'string' || typeof obj[prop] === 'number' || typeof obj[prop] === 'boolean' || obj[prop] instanceof String || obj[prop] instanceof Boolean || obj[prop] instanceof Number || obj[prop] instanceof Date )) {
          this[prop] = obj[prop];
        }
      }
      // Normalize the declared-boolean columns: the loop above keeps
      // `false`, but a tinyint arrives as the number `0` / `1`.
      copyBooleanProperties(this, obj, ChargeProfile.BOOLEAN_PROPERTIES);
    }
  }

  /**
   * True when the stored card has not yet expired. Compares against the first
   * day of the month after `expMonth/expYear` so a card valid through "12/2026"
   * remains valid until 2027-01-01.
   */
  isNotExpired(asOf: Date = new Date()): boolean {
    if (!this.expMonth || !this.expYear) { return false; }
    const expiry = new Date(this.expYear, this.expMonth, 1); // first of month AFTER expiry
    return asOf < expiry;
  }

  /** Compact display label, e.g., "Visa ending in 4242". */
  getDisplayLabel(): string {
    const brand = this.cardBrand ? this.cardBrand : 'Card';
    return this.last4 ? `${brand} ending in ${this.last4}` : brand;
  }
}


